import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import Box from 'components/Box'
import Icon from 'components/Icon'
import Text from 'components/Text'
import { ErrorMessage, LabelText, RequiredText } from 'components/TextInput'
import { getBorderColor, inputBgColor } from 'components/TextInput/SimpleTextInput'
import Spinner from 'components/Spinner'

import { ThemeColor } from 'utils/colors'

import DatetimePicker from '../'
import { CustomInputWrapperProps, DatetimeInputProps } from '.././types'
import { formatDatetime } from 'utils/datetime'
import Flex from 'components/Flex'
import InlineInputButtons from 'components/Buttons/InlineInputButtons/v2'
import useOnClickOutside from 'hooks/use-click-outside-hooks'
import InlineEditBtn from 'modules/Leads/LeadDetailV2/shared/InlineEditBtn'

const getArrow = (props: DatetimeInputProps) => (
  <Icon color={props.disabled || props.readOnly ? 'gray5' : 'blue1'} fontSize={16} icon={['far', 'calendar-alt']} />
)

const DatetimeInput: React.FC<DatetimeInputProps> = ({
  displayNode,
  value,
  dateFormat = 'PPp',
  showIcon,
  ...props
}) => {
  const [isEditDisplay, setEditBtnDisplay] = useState(false)
  const [isEditClicked, setEditClicked] = useState(false)
  const [internalDate, setInternalDate] = useState(value)

  const inlineInputRef = useRef<HTMLDivElement>(null)

  const { t } = useTranslation()

  const textProps = {
    color: (props.disabled || props.readOnly ? 'gray4' : 'defaultText') as ThemeColor,
  }

  useOnClickOutside(inlineInputRef, () => {
    if (isEditClicked) {
      setEditClicked(false)
      // onCancelClick?.()
    }

    const hasValueChanged = internalDate !== value
    if (hasValueChanged) {
      props?.onChange(internalDate ?? null)
    }
  })

  useEffect(() => {
    // keep internal date in sync with value prop, in case component is not rerendered
    setInternalDate(value)
  }, [value])

  if (displayNode && !isEditClicked) {
    return (
      <FieldBoxWrapper
        position="relative"
        onClick={() => !props.readOnly && setEditClicked(true)}
        onMouseEnter={() => setEditBtnDisplay(true)}
        onMouseLeave={() => setEditBtnDisplay(false)}
        disabled={props.disabled}
      >
        {displayNode}
        {isEditDisplay && !props.disabled && (
          <Box transform="translateY(-50%)" position="absolute" right={0} top="50%" backgroundColor="white">
            <InlineEditBtn />
          </Box>
        )}
      </FieldBoxWrapper>
    )
  }

  return (
    <Flex padding={'8px 16px'} ref={inlineInputRef}>
      <Box width="80%" onMouseDown={(e) => e.stopPropagation()}>
        <DatetimePicker
          {...props}
          {...(displayNode && {
            onChange: setInternalDate,
          })}
          dateFormat={dateFormat}
          canPickTime
          isPortaled
          popperPlacement="bottom-end"
          customInput={
            <Box>
              {props.label && (
                <LabelText>
                  {t(props.label)}
                  {props.required && <RequiredText>*</RequiredText>}
                </LabelText>
              )}
              <InputWrapper {...(props as CustomInputWrapperProps)}>
                <Flex justifyContent="space-between" width="100%">
                  <Text {...textProps}>{formatDatetime(internalDate, dateFormat)}</Text>
                  {getArrow(props)}
                </Flex>
                {showIcon && <Icon color="gray5" icon={['far', 'calendar-alt']} />}
                {props.isLoading && (
                  <LoadingOverlay>
                    <Spinner />
                  </LoadingOverlay>
                )}
              </InputWrapper>
              {props.error && <ErrorMessage>{props.error}</ErrorMessage>}
            </Box>
          }
          initialValue={value}
          showTimeSelect
        />
      </Box>
      <Box marginTop={isEditDisplay ? '12px' : undefined}>
        <InlineInputButtons
          position="relative"
          onClickCancel={() => {
            setEditBtnDisplay(false)
            setEditClicked(false)
            setInternalDate(value) // reset to prop value on cancel
          }}
          onClickConfirm={() => {
            props?.onChange(internalDate ?? null)
            setEditBtnDisplay(false)
            setEditClicked(false)
          }}
        />
      </Box>
    </Flex>
  )
}

const InputWrapper = styled.div<CustomInputWrapperProps>`
  align-items: center;
  box-sizing: border-box;
  background-color: ${(p) =>
    p.disabled || p.readOnly ? p.theme.colors.gray1 : p.fill === 'white' ? 'white' : inputBgColor};
  border: 1px solid ${(p) => getBorderColor(p)};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  padding: 16px;

  &:hover {
    cursor: ${(p) => (p.disabled || p.readOnly ? 'default' : 'pointer')};
    border-color: ${(p) => (p.disabled || p.readOnly ? p.theme.colors.gray2 : p.theme.colors.blue1)};
  }
`

const LoadingOverlay = styled.div`
  user-select: none;
  cursor: progress;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 3rem;
`

const FieldBoxWrapper = styled(Box)<{ disabled?: boolean }>`
  *:hover {
    ${({ theme, disabled }) => !disabled && `background: ${theme.colors.gray1}`};
  }
`

export default DatetimeInput
